import {
  computed,
  onScopeDispose,
  readonly,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';
import type {
  EventPricingBreakdownResponse,
  EventPricingParams,
  PricingMode,
} from '@@/types/api';
import { ApiError } from './api-error';
import { useEventsApi } from './use-events-api';

export interface UseEventPricingReturn {
  breakdown: Readonly<Ref<EventPricingBreakdownResponse | null>>;
  isInitialLoading: Readonly<Ref<boolean>>;
  isRefetching: Readonly<Ref<boolean>>;
  isLoading: ComputedRef<boolean>;
  isStale: ComputedRef<boolean>;
  error: Readonly<Ref<ApiError | null>>;
  lastUpdatedAt: Readonly<Ref<number | null>>;
  refresh: () => Promise<void>;
}

interface UseEventPricingOptions {
  isSupplemental?: MaybeRefOrGetter<boolean>;
  enabled?: MaybeRefOrGetter<boolean>;
  debounceMs?: number;
}

/**
 * Tuple of the inputs that uniquely identify a pricing request.
 * Used to detect staleness (current inputs ≠ last successful inputs).
 */
type InputSnapshot = {
  eventId: string;
  registrationTypeId: string | null | undefined;
  partySize: number;
  mode: PricingMode;
  isSupplemental: boolean | undefined;
};

function snapshotsEqual(
  a: InputSnapshot | null,
  b: InputSnapshot | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.eventId === b.eventId &&
    a.registrationTypeId === b.registrationTypeId &&
    a.partySize === b.partySize &&
    a.mode === b.mode &&
    a.isSupplemental === b.isSupplemental
  );
}

/**
 * Reactive pricing composable backed by `GET /events/:id/pricing`.
 *
 * - Debounces rapid input changes (default 250ms).
 * - Guards against out-of-order responses with a sequence counter.
 * - Preserves last-known-good `breakdown` when a fetch errors.
 * - Skips fetches when inputs are invalid (NaN/<1 party size, empty event id)
 *   or when `enabled === false`.
 */
export function useEventPricing(
  eventId: MaybeRefOrGetter<string>,
  registrationTypeId: MaybeRefOrGetter<string | null | undefined>,
  partySize: MaybeRefOrGetter<number>,
  mode: MaybeRefOrGetter<PricingMode>,
  options?: UseEventPricingOptions,
): UseEventPricingReturn {
  const debounceMs = options?.debounceMs ?? 250;

  const breakdown = ref<EventPricingBreakdownResponse | null>(null);
  const isInitialLoading = ref(false);
  const isRefetching = ref(false);
  const error = ref<ApiError | null>(null);
  const lastUpdatedAt = ref<number | null>(null);
  const lastSuccessInputs = ref<InputSnapshot | null>(null);

  const eventsApi = useEventsApi();

  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  let requestSeq = 0;

  function readInputs(): InputSnapshot {
    return {
      eventId: toValue(eventId),
      registrationTypeId: toValue(registrationTypeId),
      partySize: toValue(partySize),
      mode: toValue(mode),
      isSupplemental:
        options?.isSupplemental === undefined
          ? undefined
          : toValue(options.isSupplemental),
    };
  }

  function readEnabled(): boolean {
    return options?.enabled === undefined ? true : toValue(options.enabled);
  }

  function inputsValid(snapshot: InputSnapshot, enabled: boolean): boolean {
    if (!enabled) return false;
    if (!snapshot.eventId) return false;
    if (!Number.isFinite(snapshot.partySize)) return false;
    if (snapshot.partySize < 1) return false;
    return true;
  }

  function clearPending() {
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
  }

  async function fire(snapshot: InputSnapshot) {
    const seq = ++requestSeq;

    if (breakdown.value === null) {
      isInitialLoading.value = true;
      isRefetching.value = false;
    } else {
      isRefetching.value = true;
      isInitialLoading.value = false;
    }

    const params: EventPricingParams = {
      registration_type_id: snapshot.registrationTypeId ?? undefined,
      party_size: snapshot.partySize,
      mode: snapshot.mode,
      ...(snapshot.isSupplemental !== undefined && {
        is_supplemental: snapshot.isSupplemental,
      }),
    };

    try {
      const result = await eventsApi.getEventPricing(snapshot.eventId, params);
      // Race-condition guard — discard out-of-order responses.
      if (seq !== requestSeq) return;
      breakdown.value = result;
      lastSuccessInputs.value = snapshot;
      lastUpdatedAt.value = Date.now();
      error.value = null;
    } catch (e) {
      if (seq !== requestSeq) return;
      // Preserve last-known-good breakdown on error.
      error.value = e instanceof ApiError ? e : null;
    } finally {
      if (seq === requestSeq) {
        isInitialLoading.value = false;
        isRefetching.value = false;
      }
    }
  }

  function schedule() {
    clearPending();

    const enabled = readEnabled();
    const snapshot = readInputs();
    if (!inputsValid(snapshot, enabled)) {
      return;
    }

    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      void fire(snapshot);
    }, debounceMs);
  }

  async function refresh(): Promise<void> {
    clearPending();
    const enabled = readEnabled();
    const snapshot = readInputs();
    if (!inputsValid(snapshot, enabled)) return;
    await fire(snapshot);
  }

  watch(
    () => {
      const snap = readInputs();
      // Returning a tuple (rather than the object) so Vue's deep-equality
      // tracking treats each input as a separate dep.
      return [
        snap.eventId,
        snap.registrationTypeId,
        snap.partySize,
        snap.mode,
        snap.isSupplemental,
        readEnabled(),
      ] as const;
    },
    () => {
      schedule();
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    clearPending();
  });

  const isLoading = computed(
    () => isInitialLoading.value || isRefetching.value,
  );

  const isStale = computed(() => {
    const enabled = readEnabled();
    const current = readInputs();
    if (!inputsValid(current, enabled)) {
      // If inputs are invalid, we can't be "stale" against any successful
      // response — treat as not stale.
      return false;
    }
    return !snapshotsEqual(current, lastSuccessInputs.value);
  });

  return {
    breakdown: readonly(breakdown) as Readonly<
      Ref<EventPricingBreakdownResponse | null>
    >,
    isInitialLoading: readonly(isInitialLoading),
    isRefetching: readonly(isRefetching),
    isLoading,
    isStale,
    error: readonly(error) as Readonly<Ref<ApiError | null>>,
    lastUpdatedAt: readonly(lastUpdatedAt),
    refresh,
  };
}
